import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Lock,
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Search,
  TrendingUp,
  Users,
  DollarSign,
  Loader2,
} from "lucide-react";
import { INSURANCE_TYPES } from "@/lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "pp_admin_token";

const STATUS_META = {
  new: { label: "New", class: "bg-pp-terracotta/15 text-pp-terracotta border-pp-terracotta/30" },
  contacted: { label: "Contacted", class: "bg-amber-100 text-amber-800 border-amber-200" },
  quoted: { label: "Quoted", class: "bg-sky-100 text-sky-800 border-sky-200" },
  bound: { label: "Bound", class: "bg-pp-forest/15 text-pp-forest border-pp-forest/30" },
  lost: { label: "Lost", class: "bg-stone-200 text-stone-600 border-stone-300" },
};
const STATUS_ORDER = ["new", "contacted", "quoted", "bound", "lost"];

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const updateLead = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/admin/quotes/${id}`, patch, {
        headers: { "X-Admin-Token": token },
      });
      setQuotes((qs) => qs.map((q) => (q.id === id ? res.data : q)));
      setSelected((s) => (s && s.id === id ? res.data : s));
      toast.success("Lead updated.");
    } catch (e) {
      toast.error("Failed to update lead.");
    }
  };

  const fetchData = async (t = token) => {
    setLoading(true);
    try {
      const [qRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers: { "X-Admin-Token": t } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": t } }),
      ]);
      setQuotes(qRes.data);
      setStats(sRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired, please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load admin data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setQuotes([]);
    setStats(null);
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    return quotes.filter((q) => {
      if (typeFilter !== "all" && q.insurance_type !== typeFilter) return false;
      if (statusFilter !== "all" && (q.status || "new") !== statusFilter) return false;
      if (!s) return true;
      const hay = `${q.first_name} ${q.last_name} ${q.email} ${q.phone} ${q.state} ${q.zip_code}`.toLowerCase();
      return hay.includes(s);
    });
  }, [quotes, search, typeFilter, statusFilter]);

  if (!token) {
    return <LoginScreen onAuth={(t) => { setToken(t); localStorage.setItem(TOKEN_KEY, t); fetchData(t); }} />;
  }

  return (
    <div className="min-h-screen bg-pp-bone">
      <header className="bg-pp-forest-deep text-pp-bone">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-pp-terracotta flex items-center justify-center font-serif text-xl">
              P
            </span>
            <div>
              <div className="font-serif text-2xl leading-none">Policy Provisions</div>
              <div className="text-xs uppercase tracking-[0.2em] text-pp-bone/60 mt-1">
                Admin · Leads
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="admin-refresh-btn"
              onClick={() => fetchData()}
              variant="ghost"
              size="sm"
              className="text-pp-bone hover:bg-pp-bone/10 rounded-full"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-pp-bone hover:bg-pp-bone/10 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        {stats && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5" data-testid="admin-stats">
              <StatCard Icon={Users} label="Total leads" value={stats.total_quotes.toLocaleString()} />
              <StatCard
                Icon={DollarSign}
                label="Total coverage requested"
                value={`$${(stats.total_coverage / 1_000_000).toFixed(1)}M`}
              />
              <StatCard
                Icon={TrendingUp}
                label="Top product"
                value={
                  Object.entries(stats.by_type || {}).sort((a, b) => b[1] - a[1])[0]?.[0]?.replace("_", " ") || "—"
                }
              />
            </div>
            <ConversionWidget stats={stats} />
          </>
        )}

        <div className="bg-white border border-pp-border-subtle rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-pp-border-subtle flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pp-text-secondary" />
              <Input
                data-testid="admin-search"
                placeholder="Search by name, email, phone, state, ZIP..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="admin-type-filter" className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {INSURANCE_TYPES.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-status-filter" className="w-full sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="p-16 flex items-center justify-center text-pp-text-secondary">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading leads...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-pp-text-secondary">
              No leads match your filters yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-pp-cream text-pp-text-secondary text-xs uppercase tracking-wider">
                  <tr>
                    <Th>Lead</Th>
                    <Th>Product</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Coverage</Th>
                    <Th className="text-right">Est. Monthly</Th>
                    <Th>Location</Th>
                    <Th>Submitted</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-pp-border-subtle">
                  {filtered.map((q) => {
                    const type = INSURANCE_TYPES.find((t) => t.id === q.insurance_type);
                    return (
                      <tr key={q.id} data-testid={`lead-row-${q.id}`} className="hover:bg-pp-cream/40">
                        <Td>
                          <div className="font-medium text-pp-forest-deep">
                            {q.first_name} {q.last_name}
                          </div>
                          <div className="text-pp-text-secondary text-xs mt-0.5 flex flex-col gap-0.5">
                            <a href={`mailto:${q.email}`} className="hover:text-pp-forest flex items-center gap-1.5">
                              <Mail className="w-3 h-3" /> {q.email}
                            </a>
                            <a href={`tel:${q.phone}`} className="hover:text-pp-forest flex items-center gap-1.5">
                              <Phone className="w-3 h-3" /> {q.phone}
                            </a>
                          </div>
                        </Td>
                        <Td>
                          <Badge className="bg-pp-forest/10 text-pp-forest hover:bg-pp-forest/15 border-none">
                            {type?.name || q.insurance_type}
                          </Badge>
                          {q.term_length && (
                            <div className="text-xs text-pp-text-secondary mt-1">{q.term_length}yr term</div>
                          )}
                        </Td>
                        <Td>
                          <StatusBadge status={q.status || "new"} />
                        </Td>
                        <Td className="text-right font-serif text-lg text-pp-forest-deep">
                          ${q.coverage_amount.toLocaleString()}
                        </Td>
                        <Td className="text-right text-pp-terracotta font-medium">
                          ${q.estimated_monthly_low?.toFixed(0)}–${q.estimated_monthly_high?.toFixed(0)}
                        </Td>
                        <Td className="text-pp-text-secondary">
                          {q.state} {q.zip_code}
                        </Td>
                        <Td className="text-pp-text-secondary text-xs whitespace-nowrap">
                          {fmtDate(q.created_at)}
                        </Td>
                        <Td className="text-right">
                          <button
                            data-testid={`lead-view-${q.id}`}
                            onClick={() => setSelected(q)}
                            className="text-pp-forest hover:text-pp-forest-dark text-sm font-medium"
                          >
                            View
                          </button>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {selected && (
        <LeadDrawer
          quote={selected}
          onClose={() => setSelected(null)}
          onUpdate={(patch) => updateLead(selected.id, patch)}
        />
      )}
    </div>
  );
}

function LoginScreen({ onAuth }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pw });
      onAuth(res.data.token);
      toast.success("Welcome back, Darryl.");
    } catch (e) {
      toast.error("Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-pp-cream flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="bg-white border border-pp-border-subtle rounded-3xl p-10 w-full max-w-md shadow-[0_20px_50px_rgba(26,46,38,0.08)]"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 rounded-full bg-pp-forest flex items-center justify-center text-pp-bone font-serif text-xl">
            P
          </span>
          <div>
            <div className="font-serif text-2xl text-pp-forest-deep">Policy Provisions</div>
            <div className="text-xs uppercase tracking-[0.2em] text-pp-text-secondary mt-0.5">Admin</div>
          </div>
        </div>
        <h1 className="font-serif text-3xl text-pp-forest-deep mb-2">Sign in</h1>
        <p className="text-pp-text-secondary text-sm mb-6">
          Enter your admin password to view leads.
        </p>
        <Label className="text-pp-forest-deep font-medium mb-2 block text-sm">Password</Label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pp-text-secondary" />
          <Input
            data-testid="admin-password-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="••••••••"
            className="pl-9"
            autoFocus
          />
        </div>
        <Button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy || !pw}
          className="w-full mt-6 bg-pp-forest hover:bg-pp-forest-dark text-white rounded-full py-5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
    </div>
  );
}

function LeadDrawer({ quote, onClose, onUpdate }) {
  const type = INSURANCE_TYPES.find((t) => t.id === quote.insurance_type);
  const [notes, setNotes] = useState(quote.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    setNotes(quote.notes || "");
  }, [quote.id, quote.notes]);

  const saveNotes = async () => {
    setSavingNotes(true);
    await onUpdate({ notes });
    setSavingNotes(false);
  };

  const rows = [
    ["Email", quote.email],
    ["Phone", quote.phone],
    ["Insurance type", type?.name || quote.insurance_type],
    ["Coverage", `$${quote.coverage_amount.toLocaleString()}`],
    ...(quote.term_length ? [["Term", `${quote.term_length} years`]] : []),
    ["Estimated monthly", `$${quote.estimated_monthly_low?.toFixed(0)} – $${quote.estimated_monthly_high?.toFixed(0)}`],
    ["DOB", quote.date_of_birth],
    ["Gender", quote.gender],
    ["State / ZIP", `${quote.state} ${quote.zip_code}`],
    ["Marital status", quote.marital_status || "—"],
    ["Height / Weight", quote.height_inches != null && quote.weight_lbs != null ? `${quote.height_inches}in / ${quote.weight_lbs} lbs` : "—"],
    ["Tobacco", quote.tobacco_use],
    ["Health", (quote.health_status || "").replace("_", " ")],
    ["Annual income", `$${(quote.annual_income || 0).toLocaleString()}`],
    ["Occupation", quote.occupation || "—"],
    ["Submitted", fmtDate(quote.created_at)],
    ...(quote.updated_at ? [["Last updated", fmtDate(quote.updated_at)]] : []),
    ["Quote ID", quote.id],
  ];
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <div className="flex-1 bg-pp-forest-deep/30" onClick={onClose} />
      <aside className="w-full sm:max-w-xl bg-pp-bone overflow-y-auto">
        <div className="sticky top-0 bg-pp-bone/95 backdrop-blur border-b border-pp-border-subtle px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold">Lead detail</div>
            <h2 className="font-serif text-2xl text-pp-forest-deep mt-1">
              {quote.first_name} {quote.last_name}
            </h2>
          </div>
          <button onClick={onClose} className="text-pp-text-secondary hover:text-pp-forest-deep text-2xl leading-none">
            ×
          </button>
        </div>
        <div className="px-8 py-6 space-y-6">
          <div className="flex gap-3">
            <a href={`mailto:${quote.email}`} className="flex-1">
              <Button className="w-full bg-pp-forest hover:bg-pp-forest-dark text-white rounded-full">
                <Mail className="w-4 h-4 mr-2" /> Email
              </Button>
            </a>
            <a href={`tel:${quote.phone}`} className="flex-1">
              <Button variant="outline" className="w-full border-pp-forest text-pp-forest rounded-full bg-transparent">
                <Phone className="w-4 h-4 mr-2" /> Call
              </Button>
            </a>
          </div>

          <div className="bg-white border border-pp-border-subtle rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold mb-3">
              Pipeline status
            </div>
            <div className="grid grid-cols-5 gap-2" data-testid="drawer-status-picker">
              {STATUS_ORDER.map((s) => {
                const active = (quote.status || "new") === s;
                return (
                  <button
                    key={s}
                    data-testid={`drawer-status-${s}`}
                    onClick={() => onUpdate({ status: s })}
                    className={`text-xs py-2 px-1 rounded-full border-2 font-medium transition-all ${
                      active
                        ? "border-pp-forest bg-pp-forest text-pp-bone"
                        : "border-pp-border-subtle text-pp-text-secondary hover:border-pp-forest/40"
                    }`}
                  >
                    {STATUS_META[s].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-pp-border-subtle rounded-2xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] text-pp-forest font-bold mb-3 block">
              Internal notes
            </Label>
            <Textarea
              data-testid="drawer-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Call notes, follow-up reminders, carrier picks..."
              className="min-h-[100px]"
            />
            <Button
              data-testid="drawer-save-notes"
              onClick={saveNotes}
              disabled={savingNotes || notes === (quote.notes || "")}
              className="mt-3 bg-pp-terracotta hover:bg-pp-terracotta-dark text-white rounded-full"
              size="sm"
            >
              {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save notes"}
            </Button>
          </div>

          <div className="bg-white rounded-2xl border border-pp-border-subtle divide-y divide-pp-border-subtle">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-5 py-3 text-sm">
                <span className="text-pp-text-secondary">{k}</span>
                <span className="text-pp-forest-deep font-medium text-right break-all">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.new;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${meta.class}`}
    >
      {meta.label}
    </span>
  );
}

function StatCard({ Icon, label, value }) {
  return (
    <div className="bg-white border border-pp-border-subtle rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-pp-cream flex items-center justify-center">
          <Icon className="w-4 h-4 text-pp-forest" />
        </div>
        <div className="text-xs uppercase tracking-wider text-pp-text-secondary">{label}</div>
      </div>
      <div className="font-sans font-semibold text-4xl text-pp-forest-deep capitalize tracking-tight">{value}</div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left px-5 py-3 font-medium ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 align-top ${className}`}>{children}</td>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function ConversionWidget({ stats }) {
  const bindRatePct = ((stats.bind_rate || 0) * 100).toFixed(1);
  const commissionPct = ((stats.commission_pct || 0) * 100).toFixed(0);
  const projected = stats.projected_commission || 0;
  const avgDays = stats.avg_days_to_bind;
  const avgDaysDisplay =
    avgDays == null ? "—" : avgDays < 1 ? "<1" : `${avgDays}`;
  return (
    <div
      data-testid="conversion-widget"
      className="bg-pp-forest text-pp-bone rounded-2xl p-6 sm:p-8 mb-8 grid grid-cols-1 md:grid-cols-4 gap-6"
    >
      <div className="md:col-span-1">
        <div className="text-xs uppercase tracking-[0.2em] text-pp-bone/65 font-bold">Conversion</div>
        <div className="font-serif text-3xl mt-2 leading-tight">Pipeline health</div>
        <div className="text-sm text-pp-bone/65 mt-2">
          Live snapshot of your bind rate, sales velocity, and projected commission. Updates every refresh.
        </div>
      </div>
      <ConvStat
        label="Bind rate"
        value={`${bindRatePct}%`}
        sub={`${stats.bound_count || 0} of ${stats.total_quotes || 0} leads bound`}
        testid="cw-bind-rate"
      />
      <ConvStat
        label="Avg days new → bound"
        value={avgDaysDisplay}
        sub={avgDays != null ? "days, on average" : "No bound leads yet"}
        testid="cw-avg-days"
      />
      <ConvStat
        label="Projected commission"
        value={`$${Math.round(projected).toLocaleString()}`}
        sub={`From bound · ${commissionPct}% of yr-1 premium`}
        testid="cw-projected-commission"
        accent
      />
    </div>
  );
}

function ConvStat({ label, value, sub, testid, accent }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-pp-bone/65 font-bold">{label}</div>
      <div className={`font-sans font-semibold text-4xl mt-2 leading-none tracking-tight ${accent ? "text-pp-terracotta" : "text-pp-bone"}`}>
        {value}
      </div>
      <div className="text-xs text-pp-bone/60 mt-2">{sub}</div>
    </div>
  );
}

